import React, {useEffect, useState} from "react";
import {
    FoodEntry,
    FoodNutrient,
    FoodSource,
    FoodType,
    foodTypeFromName,
    Measurement,
    Nutrient,
    PortionSize
} from "./domain/FoodEntry";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faEdit, faThumbsUp, faTrash} from "@fortawesome/free-solid-svg-icons";
import {SelectEnum, TextEditor} from "./WebUtils";
import {getCsrfToken, isAdmin, setUserInfo} from "./App";
import {useNavigate} from "react-router-dom";

let nextNegativeId = -1;

export function WebFoodEntry() {

    let foodData = FoodEntry.EMPTY;
    const [nutriId, setNutriId] = useState<number>(0);
    const [foodPortions, setFoodPortions] = useState<PortionSize[]>([]);
    const [foodNutrients, setFoodNutrients] = useState<FoodNutrient[]>([]);
    const [category, setCategory] = useState<string>("Category");
    const [foodName, setFoodName] = useState<string>("New");
    const [foodType, setFoodType] = useState<string>("INGREDIENT");
    const [foodSource, setFoodSource] = useState<string>("");
    const [externalId, setExternalId] = useState<string>("0");
    const [publishDate, setPublishDate] = useState<Date>(new Date());
    const [publishDetails, setPublishDetails] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true); // To show a "loading" state
    const [error, setError] = useState<string | null>(null);
    const [readOnly, setReadOnly] = useState<boolean>(true);// To handle and display errors
    const [version, setVersion] = useState<number>(0);
    let navigate = useNavigate();

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const param = urlParams.get('id') || "";
        const readParam = urlParams.get('mode') ?? "view";
        const read = readParam === "view";
        setReadOnly(read);

        if (!param || param === "" || param === "0") {
            setLoading(false);
            setError(null);
            return;
        }

        fetch(`/api/v1/foodEntry/id/${param ?? nutriId}`, {
            method: "GET",
            headers: {"Content-Type": "application/json", "Accept": "application/json"},
        }).then((response) => {
            if (response.status === 401 || response.status === 403) {
                setUserInfo("Guest", "GUEST");
                navigate("/login");
            } else if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return response.json(); // Parse JSON response
        }).then((jsonData) => {
            foodData = FoodEntry.fromJson(jsonData);
            if (nutriId !== foodData.id) {
                setNutriId(foodData.id);
            }
            setPublishDetails(`Last updated: ${foodData.lastUpdated.toLocaleString()} by ${foodData.userName} (version ${foodData.version})`)
            setFoodPortions(foodData.portions);
            setFoodNutrients(foodData.nutrients);
            setCategory(foodData.category);
            setFoodName(foodData.name);
            setFoodType(foodData.foodType.name);
            setFoodSource(foodData.foodSource.name);
            setExternalId(foodData.externalId);
            setPublishDate(foodData.publicationDate);
            setVersion(foodData.version);
            setLoading(false); // Turn off loading spinner
        }).catch((err) => {
            setError(err.message); // Handle errors
            setLoading(false);
        });
    }, [nutriId]);

    if (loading) {
        return <div>Loading...</div>;
    }
    if (error) {
        return <div>Error: {error}</div>;
    }

    function onNewNutrient() {
        let nutrientToCreate = null;
        Nutrient.ENUM_VALUES.forEach(n => {
            if (!foodNutrients.find(nutrient => nutrient.nutrient === n)) {
                nutrientToCreate = n;
            }
        });
        if (nutrientToCreate) {
            const newNutrient = new FoodNutrient(nutriId, nextNegativeId--, nutrientToCreate, Measurement.GRAM, 0);
            setFoodNutrients([...foodNutrients, newNutrient]);
        } else {
            alert("All nutrients are already defined.");
        }
    }

    function addNewPortion() {
        const newPortion = new PortionSize(nutriId, nextNegativeId--, "New", "undetermined", 10);
        setFoodPortions([...foodPortions, newPortion]);
    }

    function deleteFoodNutrient(nutrient: FoodNutrient) {
        setFoodNutrients(foodNutrients.filter(n => n.foodNutrientId !== nutrient.foodNutrientId));
    }

    /**
     * Called when the user has finished editing a nutrient.
     * @param toSave {FoodNutrient} the event
     * @param newRec {boolean} true if the nutrient is new, false otherwise
     * @return {boolean} true if the nutrient can be saved, false otherwise
     */
    function editNutrientFinished(toSave: FoodNutrient, newRec: boolean) {
        if (newRec && foodNutrients.find(n => n.nutrient === toSave.nutrient)) {
            alert("Nutrient already exists.");
            return false;
        }

        setFoodNutrients(foodNutrients.map(n => n.foodNutrientId === toSave.foodNutrientId ? toSave : n));
        return true;
    }

    function performEditOperation() {
        if (!readOnly) {
            const src = FoodSource.findByName(foodSource);
            const ty = foodTypeFromName(foodType);
            if (!src || !ty || src.name === "No Source") {
                alert("Invalid source or type. Please select a valid source and type.");
                return;
            }
            if (foodNutrients.length === 0) {
                alert("At least one nutrient must be defined.");
                return;
            }
            if (foodPortions.length === 0) {
                alert("At least one portion size must be defined.");
            }
            if (foodName.length === 0 || category.length === 0) {
                alert("Name and category must be filled out.");
            }

            let foodEntry = new FoodEntry(nutriId, foodName, category, src, ty, externalId, publishDate, new Date(),
                "WebApp", version, foodPortions, foodNutrients);

            getCsrfToken().then(token => {
                fetch(`/api/v1/foodEntry/saveEntity`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(foodEntry.toJson())
                })
                    .then(response => {
                        if (!response.ok) {
                            throw new Error(`HTTP error! Status: ${response.status}`);
                        }
                        return response.json();
                    })
                    .then(data => {
                        setNutriId(data.foodEntryId);
                        alert("Record saved successfully. Please refresh the page to see the changes.!");
                    })
                    .catch(error => {
                        alert("Error saving food entry: " + error.message);
                    });
            });

        }
        setReadOnly(!readOnly);
    }

    function editPortionFinished(e: PortionSize) {
        setFoodPortions(foodPortions.map(ps => ps.portionSizeId === e.portionSizeId ? e : ps));
    }

    const editOperationButton = isAdmin() ? <button onClick={e => performEditOperation()}>
        {readOnly ? "Edit" : "Save"} Food</button> : <span/>;

    return (
        <div>
            <h1>View Nutrient Information: {(nutriId ?? 0) === 0 ? "New" : nutriId.toString()}
            </h1>
            <p>{publishDetails}</p>
            <p>
                {editOperationButton}
            </p>
            <table style={{borderCollapse: 'collapse', width: '100%', marginBottom: '20px', border: '1px solid black'}}>
                <thead>
                <tr>
                    <th>Field name</th>
                    <th>Value</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                    <td><strong>Name:</strong></td>
                    <td><TextEditor size={50} readOnly={readOnly} value={foodName}
                                    setter={e => setFoodName(e.target.value)}/></td>
                </tr>
                <tr>
                    <td><strong>Category:</strong></td>
                    <td><TextEditor size={50} readOnly={readOnly} value={category}
                                    setter={e => setCategory(e.target.value)}/></td>
                </tr>
                <tr>
                    <td><strong>External ID:</strong></td>
                    <td><TextEditor size={30} readOnly={readOnly} value={externalId}
                                    setter={e => setExternalId(e.target.value)}/></td>
                </tr>
                <tr>
                    <td><strong>Food Type:</strong></td>
                    {readOnly ? <td>{foodType}</td> :
                        <td><SelectEnum value={foodType} values={FoodType.ENUM_VALUES} onChange={v => setFoodType(v)}/>
                        </td>}
                </tr>
                <tr>
                    <td><strong>Food Source:</strong></td>
                    {readOnly ? <td>{FoodSource.findByName(foodSource)?.asWebLink()}</td> :
                        <td><SelectEnum value={foodSource} values={FoodSource.ENUM_VALUES}
                                        onChange={v => setFoodSource(v)}/></td>}
                </tr>
                <tr>
                    <td><strong>Last Published (src):</strong></td>
                    {readOnly ? <td>{publishDate.toISOString().slice(0, 10)}</td> :
                        <td><input type="date" value={publishDate.toISOString().slice(0, 10)}
                                   onChange={(e) => setPublishDate(new Date(e.target.value))}/></td>
                    }
                </tr>
                </tbody>
            </table>

            <h2>Nutrients</h2>
            {!readOnly ? <p>
                <button onClick={onNewNutrient}>Add a new nutrient</button>
            </p> : <span/>}
            <table style={{borderCollapse: 'collapse', width: '100%', border: '1px solid black'}}>
                <thead>
                <tr>
                    <th>ID</th>
                    <th>Nutrient</th>
                    <th>Quantity (per 100g)</th>
                </tr>
                </thead>
                <tbody>
                {foodNutrients && foodNutrients.map((nutrient, index) => (
                    <FoodNutrientEditor nutrient={nutrient} readOnly={readOnly}
                                        onEditFinished={(nut, isNew) => editNutrientFinished(nut, isNew)}
                                        onDeleteRecord={e => deleteFoodNutrient(nutrient)}/>))}
                </tbody>
            </table>

            <h2>Portion Sizes</h2>
            {!readOnly ? <div>
                <p>Portion sizes are used to calculate the nutritional value of recipes. Edit existing ones with care
                    as <b>adjusting them affects live recipes.</b> Further, they cannot be deleted.</p>
                <p>
                    <button onClick={addNewPortion}>Add a new portion size</button>
                </p>
            </div> : <span/>}
            <table style={{borderCollapse: 'collapse', width: '100%', border: '1px solid black'}}>
                <thead>
                <tr>
                    <th>Portion ID</th>
                    <th>Portion Name</th>
                    <th>Known Conversion</th>
                    <th>Gram Equivalent</th>
                </tr>
                </thead>
                <tbody>
                {foodPortions && foodPortions.map((ps, index) =>
                    <PortionSizeEditor portion={ps} readOnly={readOnly} onEdited={e => editPortionFinished(e)}/>)
                }
                </tbody>
            </table>
        </div>);
}

/**
 *
 * @param portion {PortionSize} size to render and edit
 * @param readOnly {boolean} if it is editing
 * @param onEdited function
 * @returns {JSX.Element}
 * @constructor
 */
export function PortionSizeEditor({portion, readOnly, onEdited}: { portion: PortionSize, readOnly: boolean, onEdited: (p: PortionSize) => void }) {
    const [portionName, setPortionName] = useState<string>(portion.portionSizeName ?? "");
    const [knownName, setKnownName] = useState<string>(portion.knownName ?? "");
    const [quantity, setQuantity] = useState<number>(portion.gramQuantity ?? 0);
    const [editing, setEditing] = useState<boolean>(false);

    function editingStarted(e: React.MouseEvent) {
        setEditing(true);
    }

    function savePortionSize(e: React.MouseEvent) {
        if (portionName?.length === 0 || knownName?.length === 0) {
            alert("Name and known name must be filled out.");
            return;
        } else if (quantity <= 0) {
            alert("Gram quantity must be greater than zero.");
            return;
        }
        portion.portionSizeName = portionName;
        portion.knownName = knownName;
        portion.gramQuantity = quantity;
        setEditing(false);
        onEdited(portion);
    }

    let editingButton = <span/>;
    if (editing) {
        editingButton =
            <button className="command" onClick={e => savePortionSize(e)}><FontAwesomeIcon icon={faThumbsUp}/></button>;
    } else if (!(readOnly ?? true)) {
        editingButton =
            <button className="command" onClick={e => editingStarted(e)}><FontAwesomeIcon icon={faEdit}/></button>;
    }

    return <tr key={portion.portionSizeId}>
        <td>
            {editingButton}
            {portion.portionSizeId === 0 ? "New" : portion.portionSizeId.toString()}
        </td>
        <td><TextEditor readOnly={readOnly || !editing} value={portionName}
                        setter={e => setPortionName(e.target.value)}/></td>
        <td><TextEditor readOnly={readOnly || !editing} value={knownName} setter={e => setKnownName(e.target.value)}/>
        </td>
        <td><TextEditor readOnly={readOnly || !editing} value={quantity}
                        setter={e => setQuantity(parseInt(e.target.value))}/></td>
    </tr>;
}

/**
 *
 * @param nutrient {FoodNutrient}
 * @param readOnly {boolean}
 * @param onEditStarted
 * @param onDeleteRecord
 * @constructor
 */
function FoodNutrientEditor({nutrient, readOnly, onEditFinished, onDeleteRecord}: { nutrient: FoodNutrient, readOnly: boolean, onEditFinished: (n: FoodNutrient, isNew: boolean) => boolean, onDeleteRecord: (e: any) => void }) {
    const newRec = nutrient.foodNutrientId <= 0;
    const [nutrientName, setNutrientName] = useState<string>(nutrient.nutrient.name ?? "");
    const [quantity, setQuantity] = useState<any>(nutrient.value ?? 0);
    const [measure, setMeasure] = useState<string>(nutrient.measurement.name ?? "g");
    const [editing, setEditing] = useState<boolean>(false);

    function startEditing(e: React.MouseEvent) {
        setEditing(true);
    }

    let editingButton = <span/>;
    if (editing) {
        editingButton =
            <button className="command" onClick={e => saveFoodNutrient(e)}><FontAwesomeIcon icon={faThumbsUp}/></button>
    } else if (!(readOnly ?? true)) {
        editingButton = <span>
            <button className="command" onClick={e => startEditing(e)}><FontAwesomeIcon icon={faEdit}/></button>
            <button className="command" onClick={e => onDeleteRecord(e)}><FontAwesomeIcon icon={faTrash}/></button>
        </span>;
    }

    function saveFoodNutrient(e: React.MouseEvent) {
        let chosenUnit = Measurement.fromString(measure);
        const chosenNutrient = Nutrient.fromString(nutrientName)
        if (!chosenUnit || !chosenNutrient || !chosenUnit.isCompatibleWith(chosenNutrient.defMeasure)) {
            alert("Invalid measurement unit. Please use a compatible unit: ");
            return;
        }
        if (quantity < 0) {
            alert("Quantity must be zero or greater.");
            return;
        }


        nutrient.nutrient = chosenNutrient;
        nutrient.measurement = chosenUnit;
        nutrient.value = parseFloat(quantity);

        if (onEditFinished(nutrient, newRec)) {
            setEditing(false);
        }
    }

    let read = readOnly || !editing;

    return <tr key={nutrient.foodNutrientId}>
        <td>{editingButton} {(newRec ? "New " : "") + nutrient.foodNutrientId.toString()}</td>
        {read || !newRec ? <td>{nutrient.nutrient.name}</td> : <td>
            <SelectEnum value={nutrientName} values={Nutrient.ENUM_VALUES} onChange={e => setNutrientName(e)}/>
        </td>}
        {read ? <td>{nutrient.value + nutrient.measurement.unit}</td> : <td>
            <input size={15} type="text" value={quantity} onChange={e => setQuantity(e.target.value)}/>
            <SelectEnum value={measure} values={Measurement.ENUM_VALUES} onChange={e => setMeasure(e)}/>
        </td>}
    </tr>
}